import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  BellOff,
  Bell,
  CheckCheck,
  Compass,
  Copy,
  DoorOpen,
  Plus,
  Settings,
} from "lucide-react";
import { cn } from "@slumbr/ui";
import { removeMember as apiRemoveMember, useUserStore } from "@slumbr/core";

import { useCommunitiesStore } from "../stores/communities-store";
import {
  ContextMenu,
  type ContextMenuAnchor,
  type ContextMenuSection,
} from "./context-menu";

/**
 * Vertical icon rail listing the communities the user belongs to.
 * Mirrors the Discord left-edge server list — round icons, vertical
 * stack, click-through to /communities/:id.
 */
export function CommunityRail() {
  const mine = useCommunitiesStore((s) => s.mine);
  const fetchMine = useCommunitiesStore((s) => s.fetchMine);
  const loadingMine = useCommunitiesStore((s) => s.loadingMine);
  const location = useLocation();
  const navigate = useNavigate();
  const myUserId = useUserStore((s) => s.user?.id);
  const [menu, setMenu] = useState<
    | {
        anchor: ContextMenuAnchor;
        community: (typeof mine)[number];
      }
    | null
  >(null);

  useEffect(() => {
    if (mine.length === 0) {
      void fetchMine();
    }
  }, [fetchMine, mine.length]);

  const activeId = matchActiveCommunityId(location.pathname);

  return (
    <nav
      data-community-rail
      className="flex h-full w-20 shrink-0 flex-col items-center gap-3 border-r border-white/[0.05] bg-[#0b0d0f] py-4"
    >
      <Link
        to="/communities"
        className={cn(
          "flex h-[52px] w-[52px] items-center justify-center rounded-2xl text-[#ff8fac] transition-colors",
          activeId === null
            ? "bg-[#ff6b8f]/15"
            : "bg-white/[0.04] hover:bg-[#ff6b8f]/12"
        )}
        title="Discover communities"
      >
        <Compass className="h-[22px] w-[22px]" />
      </Link>

      <div className="my-1.5 h-px w-9 bg-white/[0.06]" />

      <div className="flex w-full flex-1 flex-col items-center gap-3 overflow-y-auto px-2">
        {loadingMine && mine.length === 0 ? (
          <div className="mt-3 text-[11px] font-black uppercase tracking-wide text-[#71767b]">
            Loading…
          </div>
        ) : (
          mine.map((community) => {
            const initials = community.name.slice(0, 2).toUpperCase();
            const isActive = activeId === community.id;
            return (
              <Link
                key={community.id}
                to={`/communities/${community.id}`}
                title={community.name}
                onContextMenu={(event) => {
                  event.preventDefault();
                  setMenu({
                    anchor: { clientX: event.clientX, clientY: event.clientY },
                    community,
                  });
                }}
                className="group relative flex h-[52px] w-[52px] shrink-0 items-center justify-center"
              >
                {/* Inner wrapper keeps the image clipped to the circle
                 *  without trapping the active-state indicator pill or
                 *  the ring shadow. Putting `overflow-hidden` on the
                 *  outer Link would cut off the -left-1.5 pill. */}
                <div
                  className={cn(
                    "flex h-[52px] w-[52px] items-center justify-center overflow-hidden rounded-full bg-white/[0.04] text-sm font-black uppercase text-[#d7dbdc] transition-all",
                    isActive
                      ? "ring-2 ring-[#ff6b8f]/60"
                      : "group-hover:bg-[#ff6b8f]/15 group-hover:text-[#ff8fac]"
                  )}
                  style={
                    community.accentColor
                      ? { backgroundColor: `${community.accentColor}1f` }
                      : undefined
                  }
                >
                  {community.iconUrl ? (
                    <img
                      src={community.iconUrl}
                      alt=""
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    initials
                  )}
                </div>
                {isActive && (
                  <span className="absolute -left-2 top-1/2 h-8 w-1 -translate-y-1/2 rounded-full bg-[#ff6b8f]" />
                )}
              </Link>
            );
          })
        )}

        <Link
          to="/communities/new"
          className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-2xl border border-dashed border-white/[0.08] text-[#71767b] transition-colors hover:border-[#ff6b8f]/40 hover:text-[#ff8fac]"
          title="Create community"
        >
          <Plus className="h-[22px] w-[22px]" />
        </Link>
      </div>

      {menu && (
        <ContextMenu
          anchor={menu.anchor}
          onClose={() => setMenu(null)}
          sections={buildCommunityRailSections({
            community: menu.community,
            navigate,
            myUserId,
          })}
        />
      )}
    </nav>
  );
}

function buildCommunityRailSections({
  community,
  navigate,
  myUserId,
}: {
  community: {
    id: string;
    name: string;
    ownerId?: string;
    slug?: string | null;
  };
  navigate: ReturnType<typeof useNavigate>;
  myUserId: string | undefined;
}): ContextMenuSection[] {
  const muteAction = readMuteState(community.id);

  return [
    {
      items: [
        {
          id: "open",
          label: "Open community",
          icon: <DoorOpen className="h-3.5 w-3.5" />,
          onSelect: () => navigate(`/communities/${community.id}`),
        },
        {
          id: "mark-read",
          label: "Mark community as read",
          icon: <CheckCheck className="h-3.5 w-3.5" />,
          onSelect: () => {
            window.dispatchEvent(
              new CustomEvent("communities:mark-all-read", {
                detail: { communityId: community.id },
              })
            );
          },
        },
      ],
    },
    {
      items: [
        {
          id: "mute",
          label: muteAction === "mute" ? "Mute community" : "Unmute community",
          icon:
            muteAction === "mute" ? (
              <BellOff className="h-3.5 w-3.5" />
            ) : (
              <Bell className="h-3.5 w-3.5" />
            ),
          onSelect: () => {
            window.dispatchEvent(
              new CustomEvent("communities:toggle-mute", {
                detail: { communityId: community.id },
              })
            );
            writeMuteState(community.id, muteAction === "mute" ? "muted" : "active");
          },
        },
        {
          id: "copy-link",
          label: "Copy community link",
          icon: <Copy className="h-3.5 w-3.5" />,
          onSelect: () => {
            const url = `${window.location.origin}/communities/${community.slug ?? community.id}`;
            void navigator.clipboard.writeText(url);
          },
        },
      ],
    },
    {
      items: [
        {
          id: "settings",
          label: "Community settings",
          icon: <Settings className="h-3.5 w-3.5" />,
          onSelect: () => navigate(`/communities/${community.id}/settings`),
        },
        ...(community.ownerId && myUserId && community.ownerId === myUserId
          ? []
          : [
              {
                id: "leave",
                label: "Leave community",
                icon: <DoorOpen className="h-3.5 w-3.5" />,
                danger: true,
                onSelect: () => {
                  if (
                    !window.confirm(
                      `Leave ${community.name}? You'll have to be re-invited.`
                    )
                  )
                    return;
                  if (!myUserId) return;
                  void apiRemoveMember(community.id, myUserId, {
                    action: "leave",
                  })
                    .then(() => {
                      navigate("/communities");
                    })
                    .catch((err) =>
                      window.alert(
                        err instanceof Error ? err.message : "Leave failed"
                      )
                    );
                },
              },
            ]),
      ],
    },
  ];
}

// Hint stored in localStorage so the menu can show "Mute" vs "Unmute"
// without a round-trip; the actual mute state lives on the server in
// the notification-pref row and the dispatched event triggers a proper
// API call wherever the listener is wired.
function readMuteState(communityId: string): "mute" | "unmute" {
  if (typeof window === "undefined") return "mute";
  try {
    return window.localStorage.getItem(`communities:muted:${communityId}`) ===
      "muted"
      ? "unmute"
      : "mute";
  } catch {
    return "mute";
  }
}

function writeMuteState(communityId: string, value: "muted" | "active") {
  if (typeof window === "undefined") return;
  try {
    if (value === "muted") {
      window.localStorage.setItem(`communities:muted:${communityId}`, "muted");
    } else {
      window.localStorage.removeItem(`communities:muted:${communityId}`);
    }
  } catch {
    // localStorage unavailable; non-blocking
  }
}

function matchActiveCommunityId(pathname: string): string | null {
  const match = pathname.match(/^\/communities\/([^/]+)/);
  if (!match) return null;
  if (match[1] === "new" || match[1] === "join") return null;
  return match[1];
}
